import { unstable_createMuiStrictModeTheme as createTheme } from '@material-ui/core';
import { COLORS } from 'shared/constants/COLORS';

const {
  CUSTOMBLACK_DARK_CONTRASTTEXT,
  CUSTOMBLACK_DARK_DARK,
  CUSTOMBLACK_DARK_LIGHT,
  CUSTOMBLACK_DARK_MAIN,
  CUSTOMGREY_DARK_CONTRASTTEXT,
  CUSTOMGREY_DARK_DARK,
  CUSTOMGREY_DARK_LIGHT,
  CUSTOMGREY_DARK_MAIN,
  DARK_BUTTON_HOVER,
  DARK_BUTTON_BACKGROUND,
  DARK_GREEN_ICON,
  DARK_GREEN_TEXT,
  DARK_SCROLLBAR_BACKGROUND,
  DARK_TOAST_BACKGROUND,
  DARK_TOAST_TEXT,
  DEFAULT_DARK_BACKGROUND,
  DEFAULT_SWITCH_THUMB,
  DEFAULT_SWITCH_TRACK,
  ERROR_ASTERISK,
  ERROR_ICON,
  ERROR_TEXT,
  ERROR_TEXTBOX_BORDER,
  ON_SWITCH_THUMB,
  ON_SWITCH_TRACK,
  PLAIN_TEXT,
  PLAIN_TEXTBOX_INPUT,
  PRIMARY_ACTION_DARK,
  PRIMARY_BUTTON_BACKGROUND,
  PRIMARY_CHECKBOX,
  PRIMARY_DARK,
  PRIMARY_ICON,
  PRIMARY_MENUITEM_BACKGROUND,
  PRIMARY_PAPER,
  PRIMARY_SLIDER_RAIL_BACKGROUND,
  PRIMARY_SLIDER_RAIL,
  PRIMARY_SLIDER_THUMB_BORDER,
  PRIMARY_SLIDER_THUMB,
  PRIMARY_SLIDER_TRACK,
  PRIMARY_TEXT,
  PRIMARY_TEXTBOX_BORDER_FOCUS,
  SECONDARY_DARK,
  WARNING_ICON,
  DARK_FIELDS_ON_CARD_MENU,
  WARNING_TEXT,
} = COLORS;

export const darkTheme = createTheme({
  palette: {
    type: 'dark',
    background: {
      default: DEFAULT_DARK_BACKGROUND,
    },
    primary: {
      main: PRIMARY_DARK,
    },
    secondary: {
      main: SECONDARY_DARK, //main: '#10181f',
    },
    action: {
      hover: PRIMARY_ACTION_DARK, //'#3e8bff',
    },
    customGrey: {
      light: CUSTOMGREY_DARK_LIGHT, //'#c3ccd7'
      main: CUSTOMGREY_DARK_MAIN, //'#6e767f'
      dark: CUSTOMGREY_DARK_DARK, //'#98a1ac'
      contrastText: CUSTOMGREY_DARK_CONTRASTTEXT, //'#fff'
    },
    customBlack: {
      dark: CUSTOMBLACK_DARK_DARK, //'#192029'
      main: CUSTOMBLACK_DARK_MAIN, //'#0B1017'
      light: CUSTOMBLACK_DARK_LIGHT, //'#232C39'
      contrastText: CUSTOMBLACK_DARK_CONTRASTTEXT,
    },
  },

  overrides: {
    MuiButton: {
      root: {
        textTransform: 'none',
        fontSize: 13,
        backgroundColor: DARK_BUTTON_BACKGROUND,
      },
      containedPrimary: {
        backgroundColor: PRIMARY_BUTTON_BACKGROUND, // All contained buttons using `color="primary"` e.g. Log In
      },
    },
    MuiInputAdornment: {
      positionStart: {
        '& svg': {
          color: PRIMARY_ICON, // Email, password icon color
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: PRIMARY_TEXTBOX_BORDER_FOCUS, // Custom border color on focus (Login)
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: ERROR_TEXTBOX_BORDER, // Custom border on error (Login)
        },
      },
    },
    MuiTypography: {
      colorError: {
        color: ERROR_TEXT, // Password error on login
      },
    },
    MuiCheckbox: {
      root: {
        color: PRIMARY_CHECKBOX, // Login Checkbox and inside app checkbox
        '&$checked': {
          color: PRIMARY_CHECKBOX, // ensures checkmark shows via currentColor
        },
      },
      // also targets secondary variant
      colorSecondary: {
        color: PRIMARY_CHECKBOX,
        '&$checked': {
          color: PRIMARY_CHECKBOX,
        },
      },
    },
    MuiLink: {
      root: {
        marginLeft: 5,
        fontSize: 17,
        color: PRIMARY_TEXT,
        cursor: 'pointer',
        background: 'transparent',
        padding: 0,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    MuiDialogContent: {
      // root: {
      //   overflow: 'hidden !important',
      // },
    },
    MuiSwitch: {
      switchBase: {
        // Controls default (unchecked) color for the thumb
        color: `${DEFAULT_SWITCH_THUMB} !important`, //color: '#323d4e !important'
      },
      colorSecondary: {
        '&$checked': {
          // Controls checked color for the thumb
          color: ON_SWITCH_THUMB, //color: '#f2ff00',
        },
      },
      track: {
        // Controls default (unchecked) color for the track
        opacity: 0.2,
        backgroundColor: ON_SWITCH_TRACK + '!important',
        '$checked$checked + &': {
          // Controls checked color for the track
          opacity: 0.7,
          backgroundColor: `${DEFAULT_SWITCH_TRACK} !important`, //#505d6d
        },
      },
    },
    MuiSlider: {
      rail: {
        color: PRIMARY_SLIDER_RAIL,
        backgroundColor: PRIMARY_SLIDER_RAIL_BACKGROUND,
      },
      thumb: {
        border: `1px solid ${PRIMARY_SLIDER_THUMB}`,
        backgroundColor: PRIMARY_SLIDER_THUMB_BORDER,
      },
      track: {
        color: PRIMARY_SLIDER_TRACK,
      },
    },
    MuiInputLabel: {
      root: {
        color: `${PLAIN_TEXT} !important`,
      },
    },
    MuiInputBase: {
      root: {
        backgroundColor: `${DARK_FIELDS_ON_CARD_MENU} !important`,
      },
      input: {
        '&::placeholder': {
          color: `${PLAIN_TEXTBOX_INPUT} !important`,
          fontWeight: 500,
          fontSize: '12px !important',
          opacity: '0.8 !important',
        },
      },
    },
    MuiMenuItem: {
      root: {
        '&:hover, &$selected': {
          backgroundColor: `${DARK_BUTTON_HOVER} !important`,
        },
      },
    },
    MuiPaper: {
      root: {
        '& .MuiAutocomplete-option': {
          '&:hover': {
            backgroundColor: PRIMARY_PAPER,
          },
          '&[data-focus="true"]': {
            backgroundColor: PRIMARY_PAPER,
          },
        },
      },
    },
    MuiSnackbarContent: {
      root: {
        backgroundColor: DARK_TOAST_BACKGROUND,
        color: DARK_TOAST_TEXT,
        boxShadow: 'none',
        border: 'none',
      },
    },

    MuiCssBaseline: {
      '@global': {
        '*::-webkit-scrollbar-track': {
          backgroundColor: DARK_SCROLLBAR_BACKGROUND,
        },
        '.MuiFormHelperText-root.Mui-error': {
          color: ERROR_TEXT, // Email error style on login (over ride)
        },
        '.required-asterisk': {
          //BsAsterisk style
          color: ERROR_ASTERISK,
          fontSize: 10,
          marginLeft: 5,
        },
        '.icon-primary': {
          color: PRIMARY_ICON,
          cursor: 'pointer',
        },
        '.icon-warning': {
          color: WARNING_ICON,
        },
        '.icon-error': {
          color: ERROR_ICON,
        },
        '.icon-darkgreen': {
          color: DARK_GREEN_ICON,
        },
        '.warning-text': {
          color: WARNING_TEXT,
        },
        '.darkgreen-text': {
          color: DARK_GREEN_TEXT,
        },
        '.error-text': {
          color: ERROR_TEXT,
        },
      },
    },
  },
});
